import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { FileText, DollarSign, TrendingUp, CreditCard, Download } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';

export default function SalesReports() {
  const { token } = useContext(AuthContext);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('full'); // 'full', 'top_selling', 'slow_moving'
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    payment_method: 'all'
  });

  const generateReport = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/sales/reports`, filters, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(response.data);
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!reportData) return;

    const csvContent = [
      ['Sales Report - AN-NISA Nexus'],
      ['Generated:', new Date().toLocaleString()],
      [''],
      ['Summary'],
      ['Total Sales:', reportData.summary.total_sales],
      ['Total Revenue:', formatCurrency(reportData.summary.total_revenue)],
      ['Total Tax:', formatCurrency(reportData.summary.total_tax)],
      ['Total Discount:', formatCurrency(reportData.summary.total_discount)],
      ['Average Sale:', formatCurrency(reportData.summary.average_sale)],
      [''],
      ['Sale Number', 'Date', 'Customer', 'Items', 'Subtotal', 'Discount', 'Tax', 'Total', 'Payment Method'],
      ...reportData.sales.map(sale => [
        sale.sale_number,
        new Date(sale.created_at).toLocaleDateString(),
        sale.customer_name || 'Walk-in',
        sale.items.length,
        formatCurrency(sale.subtotal),
        formatCurrency(sale.discount),
        formatCurrency(sale.tax),
        formatCurrency(sale.total),
        sale.payment_method
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-report-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="sales-reports-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Sales Reports</h1>
            <p className="text-slate-600 mt-1">Detailed sales analytics and reporting</p>
          </div>
          {reportData && (
            <Button
              onClick={downloadReport}
              variant="outline"
              data-testid="download-report-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Report Filters</h2>
          <div className="grid md:grid-cols-5 gap-4">
            <div>
              <Label>Report Type</Label>
              <Select
                value={reportType}
                onValueChange={setReportType}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Report</SelectItem>
                  <SelectItem value="top_selling">Most Selling Items</SelectItem>
                  <SelectItem value="slow_moving">Slow Moving Items</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                data-testid="start-date-input"
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                data-testid="end-date-input"
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select
                value={filters.payment_method}
                onValueChange={(value) => setFilters({ ...filters, payment_method: value })}
              >
                <SelectTrigger data-testid="payment-method-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={generateReport}
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="generate-report-button"
              >
                {loading ? 'Generating...' : 'Generate Report'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Summary Cards */}
        {reportData && reportType === 'full' && (
          <>
            <div className="grid md:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Sales</p>
                    <p className="text-3xl font-bold text-slate-900">{reportData.summary.total_sales}</p>
                  </div>
                  <FileText className="w-10 h-10 text-blue-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Revenue</p>
                    <p className="text-3xl font-bold text-emerald-600">
                      {formatCurrency(reportData.summary.total_revenue)}
                    </p>
                  </div>
                  <DollarSign className="w-10 h-10 text-emerald-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Average Sale</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {formatCurrency(reportData.summary.average_sale)}
                    </p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-purple-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Tax Collected</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {formatCurrency(reportData.summary.total_tax)}
                    </p>
                  </div>
                  <CreditCard className="w-10 h-10 text-orange-600" />
                </div>
              </Card>
            </div>

            {/* By Payment Method */}
            {reportType === 'full' && (
              <Card className="p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">By Payment Method</h2>
                <div className="grid md:grid-cols-3 gap-4">
                  {Object.entries(reportData.by_payment_method).map(([method, data]) => (
                    <div key={method} className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-600 capitalize mb-1">{method}</p>
                      <p className="text-2xl font-bold text-slate-900">{data.count} sales</p>
                      <p className="text-lg text-emerald-600 font-semibold">
                        {formatCurrency(data.total)}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Most Selling Items */}
            {(reportType === 'full' || reportType === 'top_selling') && reportData.top_selling_items && reportData.top_selling_items.length > 0 && (
              <Card className="p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Most Selling Items</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 text-sm font-semibold text-slate-700">Product</th>
                        <th className="text-right py-3 px-2 text-sm font-semibold text-slate-700">Quantity Sold</th>
                        <th className="text-right py-3 px-2 text-sm font-semibold text-slate-700">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.top_selling_items.map((item, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-2 text-sm font-medium text-slate-900">
                            {item.product_name}
                          </td>
                          <td className="py-3 px-2 text-sm text-slate-600 text-right">
                            {item.quantity}
                          </td>
                          <td className="py-3 px-2 text-sm font-semibold text-emerald-600 text-right">
                            {formatCurrency(item.revenue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Slow Moving Items */}
            {(reportType === 'full' || reportType === 'slow_moving') && reportData.slow_moving_items && reportData.slow_moving_items.length > 0 && (
              <Card className="p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Slow Moving Items</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 text-sm font-semibold text-slate-700">Product</th>
                        <th className="text-right py-3 px-2 text-sm font-semibold text-slate-700">Quantity Sold</th>
                        <th className="text-right py-3 px-2 text-sm font-semibold text-slate-700">Current Stock</th>
                        <th className="text-right py-3 px-2 text-sm font-semibold text-slate-700">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.slow_moving_items.map((item, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-2 text-sm font-medium text-slate-900">
                            {item.product_name}
                          </td>
                          <td className="py-3 px-2 text-sm text-slate-600 text-right">
                            {item.quantity}
                          </td>
                          <td className="py-3 px-2 text-sm text-orange-600 text-right">
                            {item.current_stock}
                          </td>
                          <td className="py-3 px-2 text-sm text-slate-600 text-right">
                            {formatCurrency(item.revenue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Sales List */}
            {reportType === 'full' && (
              <Card className="p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">
                  Sales Transactions ({reportData.sales.length})
                </h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-2 text-sm font-semibold text-slate-700">Sale #</th>
                      <th className="text-left py-3 px-2 text-sm font-semibold text-slate-700">Date</th>
                      <th className="text-left py-3 px-2 text-sm font-semibold text-slate-700">Customer</th>
                      <th className="text-right py-3 px-2 text-sm font-semibold text-slate-700">Items</th>
                      <th className="text-right py-3 px-2 text-sm font-semibold text-slate-700">Total</th>
                      <th className="text-left py-3 px-2 text-sm font-semibold text-slate-700">Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.sales.map((sale) => (
                      <tr key={sale.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-2 text-sm font-medium text-slate-900">
                          {sale.sale_number}
                        </td>
                        <td className="py-3 px-2 text-sm text-slate-600">
                          {new Date(sale.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-2 text-sm text-slate-600">
                          {sale.customer_name || 'Walk-in Customer'}
                        </td>
                        <td className="py-3 px-2 text-sm text-slate-600 text-right">
                          {sale.items.length}
                        </td>
                        <td className="py-3 px-2 text-sm font-semibold text-emerald-600 text-right">
                          {formatCurrency(sale.total)}
                        </td>
                        <td className="py-3 px-2 text-sm text-slate-600 capitalize">
                          {sale.payment_method}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
